import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Container,
  Title,
  Text,
  Paper,
  Group,
  Stack,
  Badge,
  Button,
  Loader,
  Center,
  Table,
  Tabs,
  Progress,
  SimpleGrid,
} from "@mantine/core";
import { BarChart } from "@mantine/charts";
import {
  IconRefresh,
  IconArrowLeft,
  IconActivity,
  IconCoin,
  IconClipboardCheck,
  IconWallet,
  IconChartPie,
  IconCalendarStats,
  IconUsers,
} from "@tabler/icons-react";
import { Endpoints } from "../../../lib/endpoints";
import { PerformRequest } from "../../../hooks/usePerformRequest";

const naira = (n: number) => `₦${(n || 0).toLocaleString()}`;

const SECTIONS = [
  { value: "board", label: "Today's Board", icon: IconActivity },
  { value: "money", label: "Money Today", icon: IconCoin },
  { value: "proofs", label: "Proofs", icon: IconClipboardCheck },
  { value: "withdrawals", label: "Withdrawals", icon: IconWallet },
  { value: "budget", label: "Pilot Budget", icon: IconChartPie },
  { value: "boards", label: "Boards by Day", icon: IconCalendarStats },
  { value: "users", label: "Users & Activity", icon: IconUsers },
];

export default function Analytics() {
  const navigate = useNavigate();
  const { section } = useParams();
  const active = SECTIONS.some((s) => s.value === section) ? (section as string) : "board";
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<any>(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await PerformRequest(Endpoints.GetDashboardStats());
      if (res.status === 200 || res.status === 201) {
        setData((res.data as any).data);
      }
    } catch (error) {
      console.error("Error fetching analytics:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    document.title = "Analytics | Virel Admin";
  }, []);

  if (loading) {
    return (
      <Center h="100vh">
        <Loader size="xl" type="dots" />
      </Center>
    );
  }

  const pilot = data?.pilot;

  return (
    <Container size="xl" py="lg">
      <Group justify="space-between" align="center" mb="xl">
        <Group>
          <Button
            variant="default"
            leftSection={<IconArrowLeft size={16} />}
            onClick={() => navigate("/d")}
          >
            Overview
          </Button>
          <div>
            <Title order={2} fw={800}>
              Analytics
            </Title>
            <Text c="dimmed" size="sm">
              Every number behind the Overview cards{pilot ? ` — ${pilot.date}` : ""}.
            </Text>
          </div>
        </Group>
        <Button
          variant="default"
          leftSection={<IconRefresh size={16} />}
          onClick={fetchData}
          loading={loading}
        >
          Refresh
        </Button>
      </Group>

      {!pilot ? (
        <Paper p="xl" radius="md" withBorder>
          <Text c="dimmed" ta="center">
            The API version running doesn't provide detailed analytics yet.
          </Text>
        </Paper>
      ) : (
        <Tabs
          value={active}
          onChange={(v) => navigate(`/d/analytics/${v}`)}
          keepMounted={false}
        >
          <Tabs.List mb="lg" style={{ overflowX: "auto", flexWrap: "nowrap" }}>
            {SECTIONS.map((s) => (
              <Tabs.Tab key={s.value} value={s.value} leftSection={<s.icon size={16} />}>
                {s.label}
              </Tabs.Tab>
            ))}
          </Tabs.List>

          <Tabs.Panel value="board">
            <BoardSection pilot={pilot} />
          </Tabs.Panel>
          <Tabs.Panel value="money">
            <MoneySection pilot={pilot} />
          </Tabs.Panel>
          <Tabs.Panel value="proofs">
            <ProofsSection pilot={pilot} navigate={navigate} />
          </Tabs.Panel>
          <Tabs.Panel value="withdrawals">
            <WithdrawalsSection pilot={pilot} navigate={navigate} />
          </Tabs.Panel>
          <Tabs.Panel value="budget">
            <BudgetSection pilot={pilot} />
          </Tabs.Panel>
          <Tabs.Panel value="boards">
            <BoardsByDaySection pilot={pilot} />
          </Tabs.Panel>
          <Tabs.Panel value="users">
            <UsersSection pilot={pilot} />
          </Tabs.Panel>
        </Tabs>
      )}
    </Container>
  );
}

function HeadlineRow({ items }: { items: { label: string; value: string; hint?: string }[] }) {
  return (
    <SimpleGrid cols={{ base: 2, sm: items.length }} spacing="md" mb="lg">
      {items.map((it) => (
        <Paper key={it.label} p="md" radius="md" withBorder>
          <Text size="xs" c="dimmed" fw={700} tt="uppercase">
            {it.label}
          </Text>
          <Text fw={700} size="xl" mt={6} lh={1}>
            {it.value}
          </Text>
          {it.hint && (
            <Text size="xs" c="dimmed" mt={6}>
              {it.hint}
            </Text>
          )}
        </Paper>
      ))}
    </SimpleGrid>
  );
}

function BoardSection({ pilot }: { pilot: any }) {
  const missions = pilot.board.missions || [];
  const totalDone = missions.reduce((s: number, m: any) => s + m.completions, 0);
  const valueDelivered = missions.reduce(
    (s: number, m: any) => s + m.points * m.completions,
    0,
  );
  return (
    <>
      <HeadlineRow
        items={[
          { label: "Opportunities live", value: String(pilot.board.liveCount) },
          {
            label: "Per user",
            value: naira(pilot.board.perUserValue),
            hint: "a user who completes everything earns this",
          },
          {
            label: "Completions so far",
            value: totalDone.toLocaleString(),
            hint: `worth ${naira(valueDelivered)}`,
          },
          {
            label: "Budget cap",
            value: naira(pilot.board.budgetCap),
            hint: "if all 100 slots fill on every Opportunity",
          },
        ]}
      />
      <Paper p="md" radius="md" withBorder>
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Opportunity</Table.Th>
              <Table.Th ta="right">Reward</Table.Th>
              <Table.Th ta="right">Done</Table.Th>
              <Table.Th ta="right">Slots</Table.Th>
              <Table.Th w={160}>Fill</Table.Th>
              <Table.Th>Time left</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {missions.map((m: any) => {
              const pct = m.target > 0 ? (m.completions / m.target) * 100 : 0;
              return (
                <Table.Tr key={m.id}>
                  <Table.Td>{m.title}</Table.Td>
                  <Table.Td ta="right">₦{m.points}</Table.Td>
                  <Table.Td ta="right" fw={600}>
                    {m.completions}
                  </Table.Td>
                  <Table.Td ta="right" c="dimmed">
                    {m.target}
                  </Table.Td>
                  <Table.Td>
                    <Group gap="xs" wrap="nowrap">
                      <Progress
                        value={pct}
                        color="brand.7"
                        size="sm"
                        radius="xl"
                        style={{ flex: 1 }}
                        aria-label={`${m.title}: ${Math.round(pct)}% full`}
                      />
                      <Text size="xs" c="dimmed" w={34} ta="right">
                        {Math.round(pct)}%
                      </Text>
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    {m.completions >= m.target && m.target > 0 ? (
                      <Badge size="sm" color="orange" variant="light">
                        FULL
                      </Badge>
                    ) : m.daysLeft === 0 ? (
                      <Badge size="sm" color="yellow" variant="light">
                        ends today
                      </Badge>
                    ) : m.daysLeft !== null ? (
                      <Text size="sm" c="dimmed">
                        {m.daysLeft} day{m.daysLeft === 1 ? "" : "s"}
                      </Text>
                    ) : null}
                  </Table.Td>
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
      </Paper>
    </>
  );
}

function MoneySection({ pilot }: { pilot: any }) {
  const rows = pilot.details?.moneyTodayByMission || [];
  const capPct =
    pilot.money.budgetCapToday > 0
      ? Math.round((pilot.money.paidMissionToday / pilot.money.budgetCapToday) * 100)
      : 0;
  return (
    <>
      <HeadlineRow
        items={[
          {
            label: "Opportunity rewards",
            value: naira(pilot.money.paidMissionToday),
            hint: `${capPct}% of today's ${naira(pilot.money.budgetCapToday)} cap`,
          },
          {
            label: "Bonuses & referrals",
            value: naira(pilot.money.paidOtherToday),
          },
          {
            label: "Total paid today",
            value: naira(pilot.money.paidTotalToday),
          },
        ]}
      />
      <Paper p="md" radius="md" withBorder>
        <Group justify="space-between" mb="sm">
          <Title order={4}>Where today's money went</Title>
          <Text size="xs" c="dimmed">
            includes users finishing earlier boards still in their 3-day window
          </Text>
        </Group>
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Opportunity</Table.Th>
              <Table.Th ta="right">Payments</Table.Th>
              <Table.Th ta="right">Amount</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rows.map((r: any) => (
              <Table.Tr key={r.title}>
                <Table.Td>{r.title}</Table.Td>
                <Table.Td ta="right">{r.payments}</Table.Td>
                <Table.Td ta="right" fw={600}>
                  {naira(r.amount)}
                </Table.Td>
              </Table.Tr>
            ))}
            <Table.Tr>
              <Table.Td fw={700}>Total</Table.Td>
              <Table.Td ta="right" fw={700}>
                {rows.reduce((s: number, r: any) => s + r.payments, 0)}
              </Table.Td>
              <Table.Td ta="right" fw={700}>
                {naira(rows.reduce((s: number, r: any) => s + r.amount, 0))}
              </Table.Td>
            </Table.Tr>
          </Table.Tbody>
        </Table>
      </Paper>
    </>
  );
}

function ProofsSection({ pilot, navigate }: { pilot: any; navigate: any }) {
  const rows = pilot.details?.proofsTodayByMission || [];
  return (
    <>
      <HeadlineRow
        items={[
          { label: "Approved today", value: String(pilot.proofs.today.approved || 0) },
          { label: "Rejected today", value: String(pilot.proofs.today.rejected || 0) },
          { label: "Waiting for review", value: String(pilot.proofs.pendingQueue || 0) },
          {
            label: "Approval rate (7 days)",
            value: pilot.proofs.approvalRate7d !== null ? `${pilot.proofs.approvalRate7d}%` : "—",
          },
        ]}
      />
      <Paper p="md" radius="md" withBorder mb="md">
        <Group justify="space-between" mb="sm">
          <Title order={4}>Proofs today, per Opportunity</Title>
          <Button size="xs" variant="light" onClick={() => navigate("/d/proofs")}>
            Open Proof Submissions
          </Button>
        </Group>
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Opportunity</Table.Th>
              <Table.Th ta="right">Approved</Table.Th>
              <Table.Th ta="right">Rejected</Table.Th>
              <Table.Th ta="right">Waiting</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rows.map((r: any) => (
              <Table.Tr key={r.title}>
                <Table.Td>
                  {r.title}{" "}
                  {r.isTutorial && (
                    <Badge size="xs" variant="light" color="gray" ml={4}>
                      tutorial
                    </Badge>
                  )}
                </Table.Td>
                <Table.Td ta="right" fw={600}>
                  {r.approved}
                </Table.Td>
                <Table.Td ta="right" c={r.rejected > 0 ? "red" : "dimmed"}>
                  {r.rejected}
                </Table.Td>
                <Table.Td ta="right" c="dimmed">
                  {r.pending}
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Paper>
      <Paper p="md" radius="md" withBorder>
        <Title order={4} mb="sm">
          Recent rejections (last 7 days)
        </Title>
        <Stack gap="md">
          {(pilot.proofs.recentRejections || []).length === 0 ? (
            <Text c="dimmed" ta="center" py="md">
              No rejections in the last 7 days
            </Text>
          ) : (
            pilot.proofs.recentRejections.map((r: any, i: number) => (
              <div key={i}>
                <Group justify="space-between" wrap="nowrap">
                  <Text size="sm" fw={600}>
                    {r.mission}
                  </Text>
                  <Text size="xs" c="dimmed">
                    {new Date(r.date).toLocaleString()}
                  </Text>
                </Group>
                <Text size="sm" c="dimmed">
                  {r.reason}
                </Text>
              </div>
            ))
          )}
        </Stack>
      </Paper>
    </>
  );
}

function WithdrawalsSection({ pilot, navigate }: { pilot: any; navigate: any }) {
  const pending = pilot.details?.withdrawals?.pending || [];
  const today = pilot.details?.withdrawals?.today || [];
  const wTable = (rows: any[]) => (
    <Table striped highlightOnHover>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>User</Table.Th>
          <Table.Th ta="right">Amount</Table.Th>
          <Table.Th>Status</Table.Th>
          <Table.Th>Requested</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {rows.map((w: any, i: number) => (
          <Table.Tr key={i}>
            <Table.Td>{w.name}</Table.Td>
            <Table.Td ta="right" fw={600}>
              {naira(w.amount)}
            </Table.Td>
            <Table.Td>
              <Badge
                size="sm"
                variant="light"
                color={
                  w.status === "completed" ? "brand" : w.status === "pending" ? "orange" : "gray"
                }
              >
                {w.status}
              </Badge>
            </Table.Td>
            <Table.Td c="dimmed">{new Date(w.requestedAt).toLocaleString()}</Table.Td>
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  );
  return (
    <>
      <HeadlineRow
        items={[
          {
            label: "Awaiting approval",
            value: naira(pilot.money.pendingWithdrawals.amount),
            hint: `${pilot.money.pendingWithdrawals.count} request${
              pilot.money.pendingWithdrawals.count === 1 ? "" : "s"
            } — money about to leave`,
          },
          {
            label: "Requested today",
            value: naira(pilot.money.withdrawalsToday.amount),
            hint: `${pilot.money.withdrawalsToday.count} request${
              pilot.money.withdrawalsToday.count === 1 ? "" : "s"
            }`,
          },
        ]}
      />
      {pending.length > 0 && (
        <Paper p="md" radius="md" withBorder mb="md">
          <Title order={4} mb="sm">
            Waiting for approval
          </Title>
          {wTable(pending)}
        </Paper>
      )}
      <Paper p="md" radius="md" withBorder>
        <Group justify="space-between" mb="sm">
          <Title order={4}>Today's requests</Title>
          <Button size="xs" variant="light" onClick={() => navigate("/d/withdrawals")}>
            Open Withdrawals
          </Button>
        </Group>
        {today.length === 0 ? (
          <Text c="dimmed" ta="center" py="md">
            No withdrawal requests today
          </Text>
        ) : (
          wTable(today)
        )}
      </Paper>
    </>
  );
}

function BudgetSection({ pilot }: { pilot: any }) {
  const b = pilot.money.pilotBudget;
  const byDay = pilot.details?.pilotSpendByDay || [];
  if (!b) return <Text c="dimmed">No pilot budget data.</Text>;
  const pct = Math.round((b.spent / b.total) * 100);
  return (
    <>
      <HeadlineRow
        items={[
          { label: "Pilot budget", value: naira(b.total), hint: "100 users × ₦1,540" },
          { label: "Spent so far", value: naira(b.spent), hint: `${pct}% used` },
          { label: "Remaining", value: naira(Math.max(0, b.total - b.spent)) },
        ]}
      />
      <Paper p="md" radius="md" withBorder mb="md">
        <Progress
          value={Math.min(100, pct)}
          color={b.spent > b.total ? "red" : "brand.7"}
          size="lg"
          radius="xl"
          aria-label={`Pilot budget: ${pct}% used`}
        />
      </Paper>
      <Paper p="md" radius="md" withBorder>
        <Title order={4} mb="sm">
          Spend per day
        </Title>
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Day</Table.Th>
              <Table.Th ta="right">Payments</Table.Th>
              <Table.Th ta="right">Amount</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {byDay.map((d: any) => (
              <Table.Tr key={d.date}>
                <Table.Td>
                  {new Date(`${d.date}T12:00:00`).toLocaleDateString("en-GB", {
                    weekday: "long",
                    day: "numeric",
                    month: "long",
                  })}
                </Table.Td>
                <Table.Td ta="right">{d.payments}</Table.Td>
                <Table.Td ta="right" fw={600}>
                  {naira(d.amount)}
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Paper>
    </>
  );
}

function BoardsByDaySection({ pilot }: { pilot: any }) {
  const boards = pilot.boardsSummary || [];
  return (
    <Stack gap="md">
      {boards.map((b: any) => (
        <Paper key={b.date} p="md" radius="md" withBorder>
          <Group justify="space-between" mb="sm">
            <Group gap="sm">
              <Title order={4}>
                {new Date(`${b.date}T12:00:00`).toLocaleDateString("en-GB", {
                  weekday: "long",
                  day: "numeric",
                  month: "long",
                })}
              </Title>
              <Badge
                variant="light"
                color={
                  b.status === "open" ? "brand" : b.status === "scheduled" ? "blue" : "gray"
                }
              >
                {b.status.toUpperCase()}
              </Badge>
            </Group>
            <Text size="sm" c="dimmed">
              {b.missionCount} Opportunities · {naira(b.perUserValue)} per user ·{" "}
              {b.completions.toLocaleString()} done · {naira(b.valueDelivered)} delivered
            </Text>
          </Group>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Opportunity</Table.Th>
                <Table.Th ta="right">Reward</Table.Th>
                <Table.Th ta="right">Done</Table.Th>
                <Table.Th ta="right">Slots</Table.Th>
                <Table.Th ta="right">Value delivered</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {(b.missions || []).map((m: any) => (
                <Table.Tr key={m.title}>
                  <Table.Td>{m.title}</Table.Td>
                  <Table.Td ta="right">₦{m.points}</Table.Td>
                  <Table.Td ta="right" fw={600}>
                    {m.completions}
                  </Table.Td>
                  <Table.Td ta="right" c="dimmed">
                    {m.target}
                  </Table.Td>
                  <Table.Td ta="right">{naira(m.points * m.completions)}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Paper>
      ))}
    </Stack>
  );
}

function UsersSection({ pilot }: { pilot: any }) {
  const week = pilot.activity.week || [];
  return (
    <>
      <HeadlineRow
        items={[
          { label: "Active today", value: String(pilot.activity.activeToday) },
          { label: "New signups today", value: String(pilot.activity.signupsToday) },
          {
            label: "Active this week",
            value: String(pilot.activity.active7d),
            hint: "opened the app or logged in",
          },
          {
            label: "Monetized",
            value: `${pilot.funnel.monetized} / ${pilot.funnel.totalUsers}`,
            hint: `${pilot.funnel.stillInTutorial} not monetized`,
          },
        ]}
      />
      <Paper p="md" radius="md" withBorder mb="md">
        <Title order={4} mb="md">
          Last 7 days
        </Title>
        <BarChart
          h={280}
          data={week.map((d: any) => ({ ...d, day: d.date.slice(5) }))}
          dataKey="day"
          series={[
            { name: "active", label: "Active users", color: "#26a642" },
            { name: "signups", label: "New signups", color: "#339af0" },
          ]}
          withLegend
          tickLine="none"
          gridAxis="y"
          withYAxis={false}
        />
      </Paper>
      <Paper p="md" radius="md" withBorder mb="md">
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Day</Table.Th>
              <Table.Th ta="right">Active users</Table.Th>
              <Table.Th ta="right">New signups</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {week.map((d: any) => (
              <Table.Tr key={d.date}>
                <Table.Td>
                  {new Date(`${d.date}T12:00:00`).toLocaleDateString("en-GB", {
                    weekday: "short",
                    day: "numeric",
                    month: "short",
                  })}
                </Table.Td>
                <Table.Td ta="right" fw={600}>
                  {d.active}
                </Table.Td>
                <Table.Td ta="right">{d.signups}</Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Paper>
      {(pilot.signupCohorts || []).length > 0 && (
        <Paper p="md" radius="md" withBorder>
          <Title order={4} mb={4}>
            New users — did they come back?
          </Title>
          <Text size="xs" c="dimmed" mb="sm">
            For each day's signups: how many opened the app today, finished the
            tutorial, or have ever submitted a proof.
          </Text>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Joined on</Table.Th>
                <Table.Th ta="right">Signups</Table.Th>
                <Table.Th ta="right">Active today</Table.Th>
                <Table.Th ta="right">Monetized</Table.Th>
                <Table.Th ta="right">Submitted a proof</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {pilot.signupCohorts
                .filter((c: any) => c.signups > 0)
                .map((c: any) => (
                  <Table.Tr key={c.date}>
                    <Table.Td>
                      {new Date(`${c.date}T12:00:00`).toLocaleDateString("en-GB", {
                        weekday: "short",
                        day: "numeric",
                        month: "short",
                      })}
                    </Table.Td>
                    <Table.Td ta="right" fw={600}>
                      {c.signups}
                    </Table.Td>
                    <Table.Td ta="right" c={c.activeToday === 0 ? "red" : undefined}>
                      {c.activeToday}
                    </Table.Td>
                    <Table.Td ta="right">{c.monetized}</Table.Td>
                    <Table.Td ta="right">{c.submittedProof}</Table.Td>
                  </Table.Tr>
                ))}
            </Table.Tbody>
          </Table>
        </Paper>
      )}
    </>
  );
}
